import UTurnLeftIcon from '@mui/icons-material/UTurnLeft';
import {
  Box,
  Button,
  LinearProgress,
  Paper,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import React, { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAlert } from '../../components/alertContext';
import AlertMessageComponent from '../../components/AlertMessageComponent';
import ConfirmDialog from '../../components/ConfirmDialog';
import ButtonComponent from '../../components/form/ButtonComponent';
import SimpleAutoCompleteTextFieldComponent from '../../components/form/SimpleAutoCompleteTextFieldComponent';
import InfoDialog from '../../components/InfoDialog';
import SimpleDialogComponent from '../../components/SimpleDialogComponent';
import WarningDialog from '../../components/WarningDialog';
import IdLabelData from '../../Models/IdLabelData';
import HttpService from '../../services/HttpService';
import Constants from '../../utils/constants';
import {
  currentDatePlusDate,
  formatPrice,
  getCurrentDate,
  getFormatDate,
  roundToTwo,
} from '../../utils/storageUtil';
import { useStore } from '../../utils/store';
import DetailStep from './parts/detail-step-1';
import PricingStepSummary from './parts/pricing-step-3';
import ReviewFinalStep from './parts/review-final-step-5';
import TermsStepTab from './parts/terms-step-4';
import UploadStep from './parts/upload-step-2';

interface QuoteDialogComponentComponentProps {
  handleClose?: () => void;
  open?: boolean;
  quoteId?: any;
}

const QuoteDialogComponent: React.FC<
  QuoteDialogComponentComponentProps
> = () => {
  const steps = ['Details', 'Documents', 'Pricing', 'T & C', 'Review'];
  const isInitialLoad = useRef(true);
  const navigate = useNavigate();
  const { quoteId } = useParams<{ quoteId: any }>();
  const { getAdmin } = useStore();
  const [activeStep, setActiveStep] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(true);
  const [quoteFileDownloading, setQuoteFileDownloading] =
    React.useState<any>(null);
  const [expandRowState, setExpandRowState] = React.useState<any[]>([]);
  const [quoteStatusList, setQuoteStatusList] = React.useState<IdLabelData[]>(
    []
  );
  const [currentRecord, setCurrentRecord] = React.useState<any>(null);
  const [inductionList, setInductionList] = React.useState<any[]>([]);
  const [resourceList, setResourceList] = React.useState<any[]>([]);
  const [currentResource, setCurrentResource] = React.useState<any>(null);
  const [rejectReasonList, setRejectReasonList] = React.useState<any[]>([]);

  const [customerList, setCustomerList] = React.useState<any[]>([]);
  const [customerListOptions, setCustomerListOptions] = React.useState<any[]>(
    []
  );
  const [currentCustomer, setCurrentCustomer] = React.useState<any>(null);
  const [currentProject, setCurrentProject] = React.useState<any>(null);
  const [projectTemplateList, setProjectTemplateList] = React.useState<any[]>(
    []
  );
  const [errorMessage, setErrorMessage] = React.useState<any>(null);
  const { alert, setAlert, handleCloseAlert } = useAlert();
  const [billableList, setBillableList] = React.useState<any[]>([]);
  const [currentBillable, setCurrentBillable] = React.useState<any>(null);
  const [fileQuoteMedia, setFileQuoteMedia] = React.useState<any>([]);
  const [fileQuoteMediaCurrent, setFileQuoteMediaCurrent] = React.useState<any>(
    []
  );
  const [deletedQuoteFiles, setDeletedQuoteFiles] = React.useState<any>([]);
  const [billableDialogOpen, setBillableDialogOpen] =
    React.useState<boolean>(false);
  const [resourceDialogOpen, setResourceDialogOpen] =
    React.useState<boolean>(false);
  const [currentBillableIndexResource, setCurrentBillableIndexResource] =
    React.useState<number>(-1);

  const [approvalDialogOpen, setApprovalDialogOpen] =
    React.useState<boolean>(false);
  const [companyDetails, setCompanyDetails] = React.useState<any>(null);
  const [adhocTermsList, setAdhocTermsList] = React.useState<any[]>([]);
  const [standardTermsList, setStandardTermsList] = React.useState<any[]>([]);
  const [currentStandardTermsList, setCurrentStandardTermsList] =
    React.useState<any[]>([]);
  const [billableFormItems, setBillableFormItems] = React.useState<any[]>([]);
  const [selectedAdhocTerms, setSelectedAdhocTerms] = React.useState<any[]>([]);
  const [selectedExclusions, setSelectedExclusions] = React.useState<any[]>([]);
  const [selectedRejectReasons, setSelectedRejectReasons] = React.useState<
    any[]
  >([]);
  const [selectedQuoteInclusion, setSelectedQuoteInclusion] =
    React.useState<any>('');
  const [selectedQuoteExclusion, setSelectedQuoteExclusion] =
    React.useState<any>('');
  const [overheads, setOverheads] = React.useState<number>(0);
  const [overheadPercent, setOverheadPercent] = React.useState<number>(0);
  const [projectPercentage, setProjectPercentage] = React.useState<number>(0);
  const [projectMargin, setProjectMargin] = React.useState<number>(0);
  const [contigencyPercentage, setContigencyPercentage] =
    React.useState<number>(0);
  const GST = 10;
  const [confirmDialogOpen, setConfirmDialogOpen] =
    React.useState<boolean>(false);
  const [warningDialogOpen, setWarningDialogOpen] =
    React.useState<boolean>(false);
  const [infoDialogOpen, setInfoDialogOpen] = React.useState<boolean>(false);
  const [inProgress, setInProgress] = React.useState<boolean>(false);
  const [warningButtonTitle, setWarningButtonTitle] =
    React.useState<string>('Save');
  const [warningMessage, setWarningMessage] = React.useState<string>(
    'Are you sure you want to save as a Draft?'
  );
  const infoSuccessMessages = [
    'Success',
    'Your changes have been saved successfully.',
    'Your request has been sent for approval.',
    'Your request has been approved.',
    'Your request has been sent to the client.',
    'The quote has been rejected and sent back to the estimator for updates, along with the selected reason.',
    'The quote has been accepted and is now converted into a project.',
    'The quote has been rejected successfully with the provided reason.',
    'The quote has been accepted and is now converted into a project.',
    'Quote revision created',
  ];
  const [warningMessageTitle, setWarningMessageTitle] =
    React.useState<string>('Save Draft');
  const [infoMessageTitle, setInfoMessageTitle] =
    React.useState<string>('Success');
  const [projectForm, setProjectForm] = React.useState({
    name: '',
    notes: '',
    cost: '',
    id: null,
  });
  const maxSteps = steps.length;

  const [detailForm, setDetailForm] = React.useState<any>({
    quoteStatusId: '1',
    customerId: '',
    customerEmail: '',
    mainContact: '',
    contactNumber: '',
    id: null,
    projectName: '',
    projectWorkCategory: '',
    projectSiteAddress: '',
    estimateStartDate: getCurrentDate(),
    submissionDueDate: getCurrentDate(),
    validToDate: currentDatePlusDate(0, 3, 0),
    quoteInductionIds: '',
    projectTemplateId: '',
    internalNotes: '',
    updateVar: null,
  });
  const [mailForm, setMailForm] = React.useState<any>({
    to: detailForm.customerEmail,
    subject: '',
    body: '',
  });
  const fetchStatusList = async () => {
    try {
      const statusList: any = await fetchBundle('fetchQuoteStatuses');
      if (statusList) {
        setQuoteStatusList([{ id: '', name: 'Select Status' }, ...statusList]);
        if (quoteId === 'Add') {
          setDetailForm((prev: any) => {
            return { ...prev, quoteStatusId: '1' };
          });
        }
      }
    } catch (error: any) {
      console.error(`GET ERROR:`, error);
      setAlert({
        type: 'error',
        message: error.message,
        open: true,
      });
    }
  };
  const getResourceList = async () => {
    try {
      const response = await HttpService.get(
        Constants.apiURL + Constants.resourcesURL + '/getAllResourcesAndMedias',
        { headers: { Authorization: 'Bearer ' + getAdmin() } },
        {}
      );
      if (response.success) {
        const resourceRecords = response.data.resources;
        if (resourceRecords.length > 0) {
          //  setResourceList(resourceRecords);
          setResourceList(
            resourceRecords.map((item: any) => ({
              ...item,
              id: item.id,
              label:
                item.name +
                (item.items.length > 0
                  ? ' - ' + item.items[0].contractorName
                  : ''),
            }))
          );
          //setCurrentResource(resourceRecords[0]);
        }
      }
      //console.log('GET Response:', response);
    } catch (error: any) {
      console.error(`GET ERROR:`, error);
      setAlert({
        type: 'error',
        message: error.message,
        open: true,
      });
    }
  };
  const fetchInductionList = async () => {
    try {
      const inductionListData: any = await fetchBundle('fetchQuoteInductions');
      if (inductionListData) {
        setInductionList(inductionListData);
      }
    } catch (error: any) {
      console.error(`GET ERROR:`, error);
      setAlert({
        type: 'error',
        message: error.message,
        open: true,
      });
    }
  };
  const fetchRejectReasonsList = async () => {
    try {
      const responseData: any = await fetchBundle('fetchQuoteReasons');
      //   console.log(JSON.stringify(responseData), '----');
      if (responseData) {
        setRejectReasonList(responseData);
      }
    } catch (error: any) {
      console.error(`GET ERROR:`, error);
      setAlert({
        type: 'error',
        message: error.message,
        open: true,
      });
    }
  };
  const getRejectReasonsListText = () => {
    let reasonTxt: any[] = [];
    if (rejectReasonList && selectedRejectReasons) {
      if (rejectReasonList.length > 0 && selectedRejectReasons.length > 0) {
        reasonTxt = rejectReasonList
          .filter((item) => selectedRejectReasons.includes(item.id.toString()))
          .map((item) => item.name);
      }
    }

    return reasonTxt;
  };
  const fetchListData = async (type: string) => {
    try {
      let URL = '';
      if (type === 'customer') {
        URL = `${Constants.apiURL}${Constants.customersURL}/getQuoteCustomers`;
      }
      if (type === 'project') {
        URL = `${Constants.apiURL}${Constants.projectsURL}/getQuoteProjects`;
      }
      const response = await HttpService.get(
        URL,
        { headers: { Authorization: 'Bearer ' + getAdmin() } },
        { search: '', limit: -1, offset: 0 }
      );
      if (response.success) {
        if (type === 'customer') {
          const customerData: any = response.data.paginateRecords;
          setCustomerList(customerData);
          setCustomerListOptions(
            customerData.map((data: any) => ({ id: data.id, name: data.name }))
          );
          if (quoteId !== 'Add') {
            const customerCurrentRecord: any = customerData.find(
              (item: any) =>
                item.id.toString() === currentRecord.customerId.toString()
            );

            if (customerCurrentRecord) {
              setCurrentCustomer({
                id: customerCurrentRecord.id,
                name: customerCurrentRecord.name,
              });
            }
          }
        }
        if (type === 'project') {
          const projectData: any = response.data.paginateRecords;
          setProjectTemplateList(projectData);
          if (quoteId !== 'Add') {
            const projectDataRecord: any = projectData.find(
              (item: any) =>
                item.id.toString() ===
                currentRecord.projectTemplateId.toString()
            );
            if (projectDataRecord) {
              setCurrentProject(null);
              setCurrentProject({
                id: projectDataRecord.id,
                name: projectDataRecord.name,
              });
            }
          }
        }
      }
      //console.log('GET Response:', response);
    } catch (error: any) {
      console.error(`GET ERROR:`, error);
      setAlert({
        type: 'error',
        message: error.message,
        open: true,
      });
    }
  };
  const fetchBundle = async (type: string) => {
    return new Promise(async (resolve, reject) => {
      try {
        const URL = `${Constants.apiURL}${Constants.refBundleURL}/${type}`;
        const response = await HttpService.get(
          URL,
          { headers: { Authorization: 'Bearer ' + getAdmin() } },
          { search: '', limit: -1, offset: 0 }
        );
        if (response.data) {
          resolve(response.data);
        } else {
          reject('Failed to Fetch List. Please try again.');
        }
        //  console.log('GET Response:', response);
      } catch (error: any) {
        reject('Failed to fetch list. Please try again.');
        console.error(`GET ERROR:`, error);
        setAlert({
          type: 'error',
          message: error.message,
          open: true,
        });
      }
    });
  };
  const handleInductions = (inductionListData: any) => {
    if (inductionListData) {
      if (inductionList.length > 0) {
        const inductionListDataArr = inductionListData.split(',').map(Number);
        return inductionList.filter((item) =>
          inductionListDataArr.includes(item.id)
        );
      } else {
        return [];
      }
    } else {
      return [];
    }
  };
  const fetchStandardTerms = async () => {
    try {
      const response = await HttpService.get(
        Constants.apiURL + Constants.termsURL + '/getQuoteTerms',
        { headers: { Authorization: `Bearer ${getAdmin()}` } },
        { search: '', limit: -1, offset: 0 }
      );
      if (response.success) {
        setStandardTermsList(response.data.paginateRecords);
      }
    } catch (error: any) {
      console.error(`GET ERROR:`, error);
      setAlert({
        type: 'error',
        message: error.message,
        open: true,
      });
    }
  };
  const fetchAdhocTerms = async () => {
    try {
      const response = await HttpService.get(
        Constants.apiURL + Constants.adhocTermsURL + '/getQuoteAdhocTerms',
        { headers: { Authorization: `Bearer ${getAdmin()}` } },
        { search: '', limit: -1, offset: 0 }
      );
      if (response.success) {
        setAdhocTermsList(response.data.paginateRecords);
      }
    } catch (error: any) {
      console.error(`GET ERROR:`, error);
      setAlert({
        type: 'error',
        message: error.message,
        open: true,
      });
    }
  };

  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      loadDataList();
    }
  }, [quoteId]);
  useEffect(() => {
    if (currentRecord) {
      if (quoteId !== 'Add') {
        handleQuote(currentRecord);
        if (customerList.length === 0) fetchListData('customer');
        if (projectTemplateList.length === 0) fetchListData('project');
      }
    }
  }, [currentRecord]);
  const loadDataList = async () => {
    setIsLoading(true);
    if (inductionList.length === 0) await fetchInductionList();
    if (rejectReasonList.length === 0) await fetchRejectReasonsList();
    if (billableList.length === 0) await getBillableList();
    if (quoteId !== 'Add') {
      await fetchData(quoteId);
    }
    if (quoteStatusList.length === 0) await fetchStatusList();

    if (quoteId === 'Add') {
      if (customerList.length === 0) fetchListData('customer');
      if (projectTemplateList.length === 0) fetchListData('project');
    }
    if (adhocTermsList.length === 0) await fetchAdhocTerms();
    if (standardTermsList.length === 0) await fetchStandardTerms();
    if (resourceList.length === 0) await getResourceList();

    if (companyDetails === null) {
      await fetchCompany();
      setIsLoading(false);
    }
  };
  const handleQuote = async (quoteData: any) => {
    setProjectForm({
      name: quoteData.projectName,
      notes: '',
      cost: quoteData.quoteSubmissionPrice,
      id: null,
    });
    setDetailForm({
      quoteStatusId: quoteData.quoteStatusId.toString(),
      customerId: quoteData.customerId.toString(),
      customerEmail: quoteData.customerEmail,
      mainContact: quoteData.customerMainContact,
      contactNumber: quoteData.customerContactNumber,
      id: quoteData.id,
      projectName: quoteData.projectName,
      projectWorkCategory: quoteData.projectWorkCategory,
      projectSiteAddress: quoteData.projectSiteAddress,
      estimateStartDate: quoteData.estimateStartDate,
      submissionDueDate: quoteData.submissionDueDate,
      validToDate: quoteData.quoteValidToDate,
      quoteInductionIds: handleInductions(quoteData.quoteInductionIds),
      projectTemplateId: quoteData.projectTemplateId.toString(),
      internalNotes: quoteData.internalNotes,
      updateVar: quoteData.quoteInductionIds,
    });
    if (quoteData.customerId) {
      //console.log(customerList);
      const customerIndex: any = customerList.findIndex(
        (item: any) => item.id.toString() === quoteData.customerId.toString()
      );
      //console.log(customerIndex);
      if (customerIndex !== -1) {
        //console.log(customerList[customerIndex]);
        setCurrentCustomer({
          id: customerList[customerIndex].id,
          name: customerList[customerIndex].name,
        });
      }
    }
    setContigencyPercentage(quoteData.contigenciesPercentage ?? 0);
    if (quoteData.projectTemplateId) {
      const projectIndex: any = projectTemplateList.findIndex(
        (item: any) =>
          item.id.toString() === quoteData.projectTemplateId.toString()
      );
      if (projectIndex !== -1) {
        setCurrentProject(projectTemplateList[projectIndex]);
      }
    }
    if (quoteData.standardTerms) {
      if (quoteData.standardTerms.length > 0) {
        setCurrentStandardTermsList([]); //clear first then update to reflect changes
        setCurrentStandardTermsList(quoteData.standardTerms);
      }
    }
    setSelectedAdhocTerms(quoteData.adhocTerms);
    setSelectedQuoteExclusion(quoteData.quoteExclusions);
    setSelectedQuoteInclusion(quoteData.quoteInclusions);
    setProjectPercentage(quoteData.projectMarginPercentage);

    setFileQuoteMediaCurrent(quoteData.medias);
    handleBillableOnUpdate(quoteData.billables);
    calculateTotalCost(quoteData.billables);

    if (quoteData.billables.length > 0) {
      if (quoteId !== 'Add') {
        const filteredArray = quoteData.billables.filter(
          (item: any) => item.isExclusionSelected
        );
        setSelectedExclusions(filteredArray);
      }
    }

    setOverheads(quoteData.overheads ? parseFloat(quoteData.overheads) : 0);
    console.log(
      quoteData.overheadsPercentage,
      '<<<<<' + typeof quoteData.overheadsPercentage
    );
    setOverheadPercent(parseFloat(quoteData.overheadsPercentage));
    if (quoteData.quoteStatusId === 1) {
      setActiveStep(0);
    } else {
      if (quoteData.quoteStatusId === 5 || quoteData.quoteStatusId === 7) {
        if (quoteData.quoteStatusId === 5) {
          setSelectedRejectReasons(quoteData.internalRejectionReasonIds);
        }
        if (quoteData.quoteStatusId === 7) {
          setSelectedRejectReasons(quoteData.customerRejectionReasonIds);
        }
      }
      setActiveStep(maxSteps - 1);
    }
    getProjectPercentageValue(quoteData);
  };
  const getProjectPercentageValue = (quoteData: any) => {
    const totalCost = calculateTotalCost(quoteData.billables);
    const overheadValue = parseFloat(quoteData.overheads);
    const subTotalValue = roundToTwo(
      overheadValue + parseFloat(quoteData.contigencies) + totalCost
    );

    const projectMargiValue =
      (parseFloat(quoteData.projectMarginPercentage) * subTotalValue) / 100;

    setProjectMargin(roundToTwo(projectMargiValue));
    return roundToTwo(projectMargiValue);
  };
  const normalizeBillablePrices = (items: any[]) =>
    items.map((billable: any) => ({
      ...billable,
      resources: (billable.resources || []).map((resource: any) => ({
        ...resource,
        items: (resource.items || []).map((it: any) => ({
          ...it,
          price: formatPrice(it.price),
        })),
      })),
    }));

  const handleBillableOnUpdate = (existingBillItems: any) => {
    if (existingBillItems.length > 0 && billableList.length > 0) {
      setBillableFormItems(
        normalizeBillablePrices(
          existingBillItems.map((formItem: any) => {
            const matchedItem: any = billableList.find(
              (sourceBill: any) => sourceBill.id === formItem.billableItemId
            );
            if (matchedItem) {
              formItem.resources = formItem.resources.map(
                (resourceitemData: any) => {
                  const matchingSourceResource = matchedItem.resources.find(
                    (srcResource: any) =>
                      srcResource.id === resourceitemData.resourcesId
                  );
                  if (matchingSourceResource) {
                    resourceitemData.items = resourceitemData.items.map(
                      (item: any) => {
                        const matchingSourceItem =
                          matchingSourceResource.items.find(
                            (srcItem: any) =>
                              srcItem.id === item.resourceDetailId
                          );
                        if (matchingSourceItem) {
                          const existingMediaIds = new Set(
                            item.medias.map((media: any) => media.name)
                          );
                          const newMedias = matchingSourceItem.medias.filter(
                            (media: any) => !existingMediaIds.has(media.name)
                          );
                          item.medias = [...item.medias, ...newMedias];
                        }

                        return item;
                      }
                    );
                  }
                  return resourceitemData;
                }
              );
            }
            return formItem;
          })
        )
      );
    } else {
      setBillableFormItems(normalizeBillablePrices(existingBillItems));
    }
  };
  const fetchData = async (id: any) => {
    try {
      const response = await HttpService.get(
        Constants.apiURL + Constants.quoteURL + '/' + id,
        { headers: { Authorization: 'Bearer ' + getAdmin() } }
      );
      if (response.success) {
        setCurrentRecord(response.data);
      }

      // console.log('GET Response:', response);
    } catch (error: any) {
      console.error(`GET ERROR:`, error);
      setAlert({
        type: 'error',
        message: error.message,
        open: true,
      });
    }
  };

  const validateFields = () => {
    if (!detailForm.customerId) {
      setAlert({
        type: 'error',
        message: 'Customer is required',
        open: true,
      });
      return false;
    }
    if (!detailForm.projectName) {
      setAlert({
        type: 'error',
        message: 'Project name is required',
        open: true,
      });
      return false;
    }
    if (!detailForm.mainContact) {
      setAlert({
        type: 'error',
        message: 'Customer contact name is required',
        open: true,
      });
      return false;
    }
    if (!detailForm.customerEmail) {
      setAlert({
        type: 'error',
        message: 'Email ID is required',
        open: true,
      });
      return false;
    }
    if (!detailForm.contactNumber) {
      setAlert({
        type: 'error',
        message: 'Customer contact field is required',
        open: true,
      });
      return false;
    }
    return true;
  };
  const handleNext = () => {
    if (activeStep === 0) {
      if (!validateFields()) {
        return false;
      }
    }
    if (activeStep === 2) {
      // if (!detailForm.projectTemplateId) {
      //     setErrorMessage("Project Template  is required field");
      //     setErrorType('error');
      //     setAlertOpen(true);
      //     return false;
      // }
      if (billableFormItems.length === 0) {
        setAlert({
          type: 'error',
          message: 'Please list at least one billable',
          open: true,
        });
        return false;
      }
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };
  const handleConfirmDialog = async (type: string) => {
    if (type === 'Yes') {
      setConfirmDialogOpen(false);
      if (quoteId === 'Add') {
        if (validateFields()) await handleFinalSave(1);
        navigate(`/quote-dashboard/quote`);
      } else {
        setConfirmDialogOpen(false);
        navigate(`/quote-dashboard/quote`);
      }

      //handleClose();
      //setConfirmDialogOpen(false);
      clearForm();
    } else {
      setConfirmDialogOpen(false);
      return false;
    }
  };
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };
  const handleAttached = async (
    index: number,
    detailIndex: number,
    mediaIndex: number,
    isAttached: boolean
  ) => {
    billableFormItems[index]['resources'][detailIndex]['items'][0][
      'isAttached'
    ] = isAttached;
    billableFormItems[index]['resources'][detailIndex]['items'][0]['media'] =
      billableFormItems[index]['resources'][detailIndex]['items'][0]['medias'][
        mediaIndex
      ];
  };
  const handleArrow = async (index: number) => {
    setExpandRowState((prevItems: any) =>
      prevItems.includes(index)
        ? prevItems.filter((item: any) => item !== index)
        : [...prevItems, index]
    );
  };
  const handleBillableDetailChange = (
    value: any,
    index: number,
    type: string,
    detailIndex: number
  ) => {
    console.log(value, index, type, detailIndex);
    console.log(JSON.stringify(billableFormItems));
    setBillableFormItems((prevItems) => {
      const updatedItems = JSON.parse(JSON.stringify(prevItems));
      updatedItems[index]['resources'][detailIndex]['items'][0] = {
        ...updatedItems[index]['resources'][detailIndex]['items'][0],
        [type]: value,
      };
      calculateTotalCost(updatedItems);
      return updatedItems;
    });
  };
  const handleResourceChange = (e: any, dataValue: any) => {
    setCurrentResource(dataValue);
  };
  const handleBillableChange = (value: any, index: number, type: string) => {
    setBillableFormItems((prevItems) => {
      // Create a copy of the current state
      const updatedItems = [...prevItems];
      // Update the specific item's property
      updatedItems[index] = {
        ...updatedItems[index],
        [type]: value,
      };
      calculateTotalCost(updatedItems);
      return updatedItems;
    });
  };
  const handleBillableBlur = (value: any, index: number, type: string) => {
    if (value > 0) {
      for (var i = 0; i < billableFormItems[index]['resources'].length; i++) {
        handleBillableDetailChange(
          billableFormItems[index]['resources'][i]['items'][0].quantity * value,
          index,
          'quantity',
          i
        );
      }
    }
    setBillableFormItems((prevItems) => {
      // Create a copy of the current state
      const updatedItems = [...prevItems];
      // Update the specific item's property
      updatedItems[index] = {
        ...updatedItems[index],
        isDisabled: true,
      };

      return updatedItems;
    });
  };

  const removeDetailsItem = (index: any, detailIndex: any) => {
    setBillableFormItems((prevItems) => {
      // Create a deep copy of the state array
      const newItems = prevItems.map((item, i) => {
        if (i === index) {
          return {
            ...item,
            resources: item.resources.filter(
              (_: any, dIndex: any) => dIndex !== detailIndex
            ),
          };
        }
        return item;
      });
      if (newItems[index].resources.length === 0) {
        removeItem(index);
      }
      calculateTotalCost(newItems); // Call your function to calculate total cost
      return newItems; // Return the updated array
    });
  };
  const calculateTotalCost = (newItems: any) => {
    const totalCost = newItems.reduce((acc: number, item: any) => {
      const itemCost = item.resources.reduce(
        (detailAcc: number, detail: any) => {
          return (
            detailAcc +
            roundToTwo(
              parseInt(detail['items'][0].quantity) *
                parseFloat(detail['items'][0].price)
            )
          );
        },
        0
      );
      return acc + itemCost;
    }, 0);
    setProjectForm((prev: any) => {
      return { ...prev, cost: isNaN(totalCost) ? 0.0 : totalCost };
    });
    return totalCost;
  };
  const removeItem = (index: any) => {
    setBillableFormItems((prevItems) => {
      const newItems = [...prevItems];
      newItems.splice(index, 1);
      calculateTotalCost(newItems);
      return newItems;
    });
    /* setBillableRemovedItems((prevItems) => {
             const newItems = [...prevItems];
             if (!newItems.includes(billableFormItems[index])) {
                 newItems.push(billableFormItems[index]);
             }
             return newItems;
         });*/
  };
  const fetchProject = async (id: string) => {
    try {
      const response = await HttpService.get(
        `${
          Constants.apiURL + Constants.billableURL
        }/getProjectBillableWithResources/${id}`,
        { headers: { Authorization: `Bearer ${getAdmin()}` } },
        null
      );
      if (response.success) {
        handleProjectRecord(response.data);
      }
    } catch (error: any) {
      console.error(`GET ERROR:`, error);
      setAlert({
        type: 'error',
        message: error.message,
        open: true,
      });
    }
  };
  // Fetch single term record
  const fetchCompany = async () => {
    try {
      const response = await HttpService.get(
        `${Constants.apiURL + Constants.fetchQuoteCompanyProfile}`,
        { headers: { Authorization: `Bearer ${getAdmin()}` } },
        null
      );
      if (response.data) {
        const compData: any = response.data;
        if (quoteId === 'Add')
          setOverheadPercent(
            compData
              ? compData.financial.length > 0
                ? compData.financial[0].overheadPercent
                : 0
              : 0
          );
      }
    } catch (error: any) {
      console.error(`GET ERROR:`, error);
      setAlert({
        type: 'error',
        message: error.message,
        open: true,
      });
    }
  };

  const handleProjectRecord = (record: any) => {
    if (record.projectTemplateId) {
      handleBillables(record.billables);
    }
  };

  const handleBillables = (billables: any) => {
    const normalizedBillables = normalizeBillablePrices(billables);
    let billableItemsData: any[] = [];
    if (billableFormItems.length === 0) {
      setBillableFormItems(normalizedBillables);
      calculateTotalCost(normalizedBillables);
      return;
    } else {
      billableItemsData = billableFormItems;
      for (var i = 0; i < normalizedBillables.length; i++) {
        const indexData = billableItemsData.findIndex(
          (item: any) =>
            (item.billableItemId
              ? item.billableItemId.toString()
              : item.id.toString()) === normalizedBillables[i].id.toString()
        );
        if (indexData === -1) {
          billableItemsData.push(normalizedBillables[i]);
          continue;
        }
      }
      setBillableFormItems(billableItemsData);
      calculateTotalCost(billableItemsData);
      return;
    } //this needs to confirm
  };

  const handleFileChange = (files: any) => {
    if (files) {
      setFileQuoteMedia((prevFiles: any) => [
        ...prevFiles,
        ...Array.from(files),
      ]);
    }
  };

  const handleChangeDate = (value: any, name: string) => {
    const DateValue =
      value.year() + '-' + (value.month() + 1) + '-' + value.date();
    setDetailForm((prev: any) => {
      return { ...prev, [name]: DateValue };
    });
  };
  const handleRemoveFile = (file: any) => {};
  const handleCurrentFilesRemove = (fileData: any) => {
    const removedFiles = deletedQuoteFiles;
    const updatedFiles = fileQuoteMediaCurrent.filter(
      (file: any) => file !== fileData
    ); // Create a new array
    setFileQuoteMediaCurrent(updatedFiles);
    removedFiles.push(fileData);
    setDeletedQuoteFiles(removedFiles);
  };

  const getBillableList = async () => {
    try {
      const response = await HttpService.get(
        Constants.apiURL +
          Constants.billableURL +
          '/getAllBillableWithResourcesAndMedias',
        { headers: { Authorization: 'Bearer ' + getAdmin() } },
        {}
      );
      if (response.success) {
        const billableRecords = response.data.billables;
        if (billableRecords.length > 0) {
          const sortedData = billableRecords.sort((a: any, b: any) =>
            a.name.localeCompare(b.name)
          );
          setBillableList(sortedData);
          setCurrentBillable(billableRecords[0]);
        }
      }
      // console.log('GET Response:', response);
    } catch (error: any) {
      console.error(`GET ERROR:`, error);
      setAlert({
        type: 'error',
        message: error.message,
        open: true,
      });
    }
  };
  const handleAutoCompleteChange = (
    e: any,
    dataValue: any,
    typeData: string
  ) => {
    if (typeData === 'customerId') {
      setCurrentCustomer({ id: dataValue.id, name: dataValue.name });
      const currentData = customerList.find(
        (item: any) => item.id.toString() === dataValue.id.toString()
      );

      setDetailForm((prev: any) => ({
        ...prev,
        mainContact: currentData.tradingName.toString(),
        customerEmail: currentData.email,
        contactNumber: currentData.phone,
        customerId: dataValue.id.toString(),
      }));
    }
    if (typeData === 'projectTemplateId') {
      setCurrentProject(dataValue);
      fetchProject(dataValue.id.toString());
      setDetailForm((prev: any) => {
        return { ...prev, [typeData]: dataValue.id.toString() };
      });
    }
  };
  const handleInductionChange = (e: any, dataValue: any) => {
    setDetailForm((prev: any) => {
      return { ...prev, quoteInductionIds: dataValue };
    });
  };
  const handleDetailInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLFormElement
    >
  ) => {
    const { name, value } = e.target;

    setDetailForm((prev: any) => {
      return { ...prev, [name]: value };
    });
  };

  const handleBillableAdd = (e: any, dataValue: any) => {
    setCurrentBillable(dataValue);
  };

  const AddBillableItem = () => {
    const index: any = billableList.findIndex(
      (item: any) => item.id.toString() === currentBillable.id.toString()
    );
    const [normalized] = normalizeBillablePrices([billableList[index]]);
    const item = {
      ...normalized,
      keyidx: `${Date.now()}-${Math.floor(Math.random() * 10000)}`,
    };
    setBillableFormItems((prevItems) => {
      const newItems = [...prevItems];
      newItems.push(item);
      calculateTotalCost(newItems);
      return newItems;
    });
    // if (index !== -1) {
    //   const indexData = billableFormItems.findIndex((item: any) =>
    //     item.billableItemId
    //       ? item.billableItemId.toString() === currentBillable.id.toString()
    //       : item.id.toString() === currentBillable.id.toString()
    //   );
    //   if (indexData === -1) {
    //     setBillableFormItems((prevItems) => {
    //       const newItems = [...prevItems];
    //       if (!newItems.includes(billableList[index])) {
    //         newItems.push(billableList[index]);
    //       }
    //       calculateTotalCost(newItems);
    //       return newItems;
    //     });
    //   }
    // }
    setBillableDialogOpen(false);
  };
  const AddResourceItem = () => {
    if (currentBillableIndexResource !== -1) {
      const indexData = billableFormItems[currentBillableIndexResource][
        'resources'
      ].findIndex((item: any) =>
        item.resourcesId
          ? item.resourcesId.toString() === currentResource.id.toString()
          : item.id.toString() === currentResource.id.toString()
      );
      if (indexData === -1) {
        const normalizedResource = {
          ...currentResource,
          items: (currentResource.items || []).map((it: any) => ({
            ...it,
            price: formatPrice(it.price),
          })),
        };
        setBillableFormItems((prevItems) => {
          const newItems = [...prevItems];
          if (
            !newItems[currentBillableIndexResource]['resources'].includes(
              normalizedResource
            )
          ) {
            newItems[currentBillableIndexResource]['resources'].push(
              normalizedResource
            );
          }
          calculateTotalCost(newItems);
          return newItems;
        });
      }
    }
    setResourceDialogOpen(false);
  };
  const handleSave = async (reqData: any) => {
    const endpoint = `${Constants.apiURL + Constants.adhocTermsURL}`;
    let response: any;
    try {
      response = await HttpService.post(endpoint, reqData, {
        headers: { Authorization: `Bearer ${getAdmin()}` },
      });
      if (response.success) {
        if (reqData.isActive) {
          handleTermSelect([response.data], 'newupdate');
        }
      }
    } catch (error: any) {
      console.error(`POST ERROR:`, error);
      setAlert({
        type: 'error',
        message: error.message,
        open: true,
      });
    }
  };
  const handleQuoteIncluExclusionSelect = (rowData: any, type: string) => {
    if (type === 'inclusion') {
      setSelectedQuoteInclusion(rowData);
    }
    if (type === 'exclusion') {
      setSelectedQuoteExclusion(rowData);
    }
  };
  const handleBillableExclusionSelect = (rowData: any) => {
    const currentIndex = selectedExclusions.indexOf(rowData);
    const newChecked = [...selectedExclusions];

    if (currentIndex === -1) {
      newChecked.push(rowData);
    } else {
      newChecked.splice(currentIndex, 1);
    }
    // console.log(`newChecked`);
    // console.log(newChecked);
    // console.log(`newChecked`);
    setSelectedExclusions(newChecked);
  };
  const handleTermSelect = (rowData: any, type: string) => {
    if (type === 'new') {
      handleSave(rowData);
    } else if (type === 'newupdate') {
      setSelectedAdhocTerms((prev) => {
        const existingIds = new Set(prev.map((prevRow) => prevRow.id));
        const filteredRows = rowData.filter(
          (row: any) => !existingIds.has(row.id)
        );
        return [...prev, ...filteredRows];
      });
    } else {
      setSelectedAdhocTerms(rowData);
    }
  };
  const getSubtotal = () => {
    const directSubtotal = roundToTwo(
      projectForm.cost ? parseFloat(projectForm.cost) : 0
    );
    const indirectSubtotal = roundToTwo(
      (overheads ?? 0) + parseFloat(getContigencyPercentageValue())
    );
    return roundToTwo(directSubtotal + indirectSubtotal);
  };
  const getSubmissionPrice = () => {
    const subTotal = getSubtotal();
    return subTotal + (projectMargin ? projectMargin : 0);
  };
  const getContigencyPercentageValue = () => {
    const subTotal = parseFloat(projectForm.cost ? projectForm.cost : '0');
    // console.log('contigencyPercentage == ' + contigencyPercentage);
    const percentage =
      ((contigencyPercentage ? contigencyPercentage : 0) * subTotal) / 100;
    return roundToTwo(percentage);
  };
  const getStatusText = (quoteStatusId: any) => {
    const statusData: any = quoteStatusList.find(
      (item: any) => item.id.toString() === quoteStatusId.toString()
    );
    if (statusData) {
      return statusData.name;
    } else {
      return quoteStatusId;
    }
  };

  const getInductionIDS = (inductionId: any[]) => {
    if (inductionId) return inductionId.map((item: any) => item.id).join(',');
    else return '';
  };
  const getGST = () => {
    const GSTTotal = (getSubmissionPrice() * GST) / 100;
    return GSTTotal ?? 0;
  };
  const clearForm = () => {
    setActiveStep(0);
    setCurrentCustomer(null);
    setCurrentProject(null);
    setDetailForm({
      quoteStatusId: '1',

      customerId: '',
      customerEmail: '',
      mainContact: '',
      contactNumber: '',
      id: null,
      projectName: '',
      projectWorkCategory: '',
      projectSiteAddress: '',
      estimateStartDate: getCurrentDate(),
      submissionDueDate: getCurrentDate(),
      validToDate: currentDatePlusDate(20, 3, 0),
      quoteInductionIds: '',
      projectTemplateId: '',
      internalNotes: '',
      updateVar: null,
    });
    setProjectForm({
      name: '',
      notes: '',
      cost: '',
      id: null,
    });
    setFileQuoteMedia([]);
    setFileQuoteMediaCurrent([]);

    setSelectedAdhocTerms([]);
    setSelectedExclusions([]);
    setSelectedQuoteInclusion('');
    setBillableFormItems([]);
    setSelectedQuoteExclusion('');
    setInProgress(false);
    navigate('/quote-dashboard/quote');
    // handleClose();
  };

  // TODO: Remove function?
  const handleDownloadFile = async (id: any) => {
    setQuoteFileDownloading(id);
    await HttpService.handleDownloadFile(id, getAdmin());
    setQuoteFileDownloading(null);
  };

  const handleFinalSummary = async (type: any) => {
    if (type === -1) {
      setWarningButtonTitle('Delete');
      setWarningMessage(
        'Are you sure you want to delete this quote? This action cannot be undone.'
      );
      setWarningMessageTitle('Delete Quote');
    }
    if (type === 0) {
      setWarningButtonTitle('Yes');
      setWarningMessage(
        'If you cancel, your changes will not be saved. Do you want to continue?'
      );
      setWarningMessageTitle('Cancel');
    }
    if (type === 1) {
      setWarningButtonTitle('Save');
      setWarningMessage('Are you sure you want to save as a Draft?');
      setWarningMessageTitle('Save Draft');
    }
    if (type === 2) {
      setWarningButtonTitle('Send');
      setWarningMessage('Are you sure you want to send this for approval?');
      setWarningMessageTitle('Send for Internal Approval');
    }
    if (type === 3) {
      setWarningButtonTitle('Approve');
      setWarningMessage('Are you sure you want to approve this quote?');
      setWarningMessageTitle('Approve');
    }
    if (type === 4) {
      setWarningButtonTitle('Send to Client');
      setWarningMessage(
        'Are you sure you want to send this quote to the client?'
      );
      setWarningMessageTitle('Send to Client');
    }
    if (type === 5) {
      setWarningButtonTitle('Reject');
      setWarningMessage(
        'Are you sure you want to reject this quote and send it back to the estimator for updates? Please select a reason for rejection.'
      );
      setWarningMessageTitle('Reject');
    }
    if (type === 6) {
      setWarningButtonTitle('Quote Accepted');
      setWarningMessage(
        'Are you sure this quote has been accepted by the client? The system will now convert it into a project.'
      );
      setWarningMessageTitle('Client Accepted Quote');
    }
    if (type === 7) {
      setWarningButtonTitle('Quote Rejected');
      setWarningMessage(
        'Are you sure you want to mark this quote as rejected? Please provide a reason for the rejection.'
      );
      setWarningMessageTitle('Client Rejected Quote');
    }
    if (type === 9) {
      setWarningButtonTitle('Revision');
      setWarningMessage(
        'Are you sure you want to create a revision for this quote?.'
      );
      setWarningMessageTitle('Revision');
    }
    if (type === 8) {
      setWarningButtonTitle('Quote Internally Accepted');
      setWarningMessage(
        'Are you sure this quote has been accepted Internally? The system will now convert it into a project.'
      );
      setWarningMessageTitle('Internally Accepted Quote');
    }

    setWarningDialogOpen(true);
    return;
  };
  const handleWarningClose = async (type: any, reasons?: any[]) => {
    if (type === 'Yes') {
      //Cancel the quote without saving it
      setWarningDialogOpen(false);
      setInfoDialogOpen(true);
      setInfoMessageTitle('Changes have been discarded.');

      return;
    }
    if (type === 'Delete') {
      //Delete the quote without saving it
      setWarningDialogOpen(false);
      await deleteQuote(quoteId);

      return;
    }
    if (type === 'Cancel') {
      setWarningDialogOpen(false);
    }
    //save as draft
    if (type === 'Save') {
      handleFinalSave(1);
      setWarningDialogOpen(false);
    }

    //send for internal approve
    if (type === 'Send') {
      handleFinalSave(2);
      setWarningDialogOpen(false);
    }
    //internal approved
    if (type === 'Approve') {
      handleFinalSave(3);
      setWarningDialogOpen(false);
    }
    //internal rejected
    if (type === 'Reject') {
      console.log(reasons);
      if (reasons) setSelectedRejectReasons(reasons);
      handleFinalSave(5, reasons);
      setWarningDialogOpen(false);
    }
    //Send to client only if internal approved
    if (type === 'Send to Client') {
      setApprovalDialogOpen(true);
      setMailForm((prev: any) => {
        return {
          ...prev,
          to: currentRecord.customerEmail,
          subject:
            'Quote No ' +
            (currentRecord.rootQuoteId
              ? currentRecord.rootQuoteId + '.' + currentRecord.revisionNumber
              : currentRecord.id) +
            ' from Loumac',
        };
      });
      setWarningDialogOpen(false);
    }
    //Customer accepted quote
    if (type === 'Quote Accepted') {
      handleFinalSave(6);
      setWarningDialogOpen(false);
    }
    //Customer rejected quote
    if (type === 'Quote Rejected') {
      handleFinalSave(7, reasons);
      setWarningDialogOpen(false);
      if (reasons) setSelectedRejectReasons(reasons);
    }
    if (type === 'Quote Internally Accepted') {
      handleFinalSave(8);
      setWarningDialogOpen(false);
    }
    if (type === 'Revision') {
      handleFinalSave(9);
      setWarningDialogOpen(false);
    }
  };
  const handleForApproval = async () => {
    try {
      if (!mailForm.to.trim()) {
        setAlert({
          type: 'error',
          message: 'Email address is required',
          open: true,
        });
        return false;
      }

      setInProgress(true);
      const endpoint = `${Constants.apiURL}/email/sendQuoteEmailToClient`;
      const response = await HttpService.post(
        endpoint,
        {
          toemails: mailForm.to.split(','),
          htmlbody: mailcontentHTML(),
          subject: mailForm.subject,
          quoteId: detailForm.id,
        },
        {
          headers: { Authorization: `Bearer ${getAdmin()}` },
        }
      );
      if (response.success) {
        setInfoDialogOpen(true);
        setInfoMessageTitle(infoSuccessMessages[4]);

        //clearForm(); ̰
      }
    } catch (error: any) {
      console.error(`GET ERROR:`, error);
      setAlert({
        type: 'error',
        message: error.message,
        open: true,
      });
      setInProgress(false);
      navigate('/quote-dashboard/quote');
    }
  };
  const handleFinalSave = async (type: any, reasonsList?: any[]) => {
    if (type === 9) {
      await createRevision();
      return false;
    }
    if ([4, 6, 8].includes(type)) {
      await updateStatus(detailForm.id, type);
      return false;
    }
    const quoteDataForm: any = {
      quoteStatusId: type,

      customerId: currentCustomer.id,
      id: detailForm.id,
      customerName: currentCustomer.name,
      customerEmail: detailForm.customerEmail,
      customerMainContact: detailForm.mainContact,
      customerContactNumber: detailForm.contactNumber,
      projectName: detailForm.projectName,
      projectWorkCategory: detailForm.projectWorkCategory,
      projectSiteAddress: detailForm.projectSiteAddress,
      estimateStartDate: detailForm.estimateStartDate,
      submissionDueDate: detailForm.submissionDueDate,
      quoteInductionIds: getInductionIDS(detailForm.quoteInductionIds),
      projectTemplateId: currentProject ? currentProject.id : 0,
      quoteValidToDate: detailForm.validToDate,
      projectMarginPercentage: projectPercentage,
      quoteSubmissionPrice: roundToTwo(getSubmissionPrice()),
      quoteGSTPrice: roundToTwo(getGST()),
      quoteTotalPrice: roundToTwo(
        roundToTwo(getSubmissionPrice()) + roundToTwo(getGST())
      ),
      internalNotes: detailForm.internalNotes,
      quoteInclusions: selectedQuoteInclusion,
      quoteExclusions: selectedQuoteExclusion,
      contigenciesPercentage: roundToTwo(contigencyPercentage),
      overheadsPercentage: roundToTwo(overheadPercent),

      adhocTerms: [],
      billables: {},
      overheads: overheads ? roundToTwo(overheads).toFixed(2) : '0',
      contigencies: roundToTwo(getContigencyPercentageValue()).toFixed(2),
    };
    if (quoteId !== 'Add') {
      quoteDataForm['medias'] = fileQuoteMediaCurrent;
      if (type === 5 || type === 7) {
        if (type === 5)
          quoteDataForm['internalRejectionReasonIds'] = reasonsList
            ? reasonsList.join(',')
            : '';
        if (type === 7)
          quoteDataForm['customerRejectionReasonIds'] = reasonsList
            ? reasonsList.join(',')
            : '';
      }
    }

    // console.log(quoteDataForm);
    // return false;
    const billableData: any = [];
    for (var i = 0; i < billableFormItems.length; i++) {
      const resourceDetails = billableFormItems[i]['resources'];
      let lineTotal = 0;
      const detailsData = [];
      for (let j = 0; j < resourceDetails.length; j++) {
        lineTotal +=
          parseFloat(resourceDetails[j]['items'][0]['price']) *
          parseInt(resourceDetails[j]['items'][0]['quantity']);
        detailsData.push({
          resourcesId:
            resourceDetails[j]['resourcesId'] ?? resourceDetails[j]['id'],
          name: resourceDetails[j]['name'],
          expiryDate: resourceDetails[j]['expiryDate'],
          isQuote: resourceDetails[j]['isQuote'],
          resourceTypeId: resourceDetails[j]['resourceTypeId'],
          unitTypeId: resourceDetails[j]['unitTypeId'] || 0,
          resourceTotalPrice: (
            parseFloat(resourceDetails[j]['items'][0]['price']) *
            parseInt(resourceDetails[j]['items'][0]['quantity'])
          ).toString(),
          items: [
            {
              resourceDetailId:
                resourceDetails[j]['items'][0]['resourceDetailId'] ??
                resourceDetails[j]['items'][0]['id'],
              contractorId: resourceDetails[j]['items'][0]['contractorId'],
              notes: resourceDetails[j]['items'][0]['Note2'],
              isAttached: resourceDetails[j]['items'][0]['isAttached']
                ? resourceDetails[j]['items'][0]['isAttached']
                : 0, //todo
              resourceItemsTotalPrice: (
                parseFloat(resourceDetails[j]['items'][0]['price']) *
                parseInt(resourceDetails[j]['items'][0]['quantity'])
              ).toString(),
              price: resourceDetails[j]['items'][0]['price'],
              quantity: parseInt(resourceDetails[j]['items'][0]['quantity']),

              media: resourceDetails[j]['items'][0]['isAttached']
                ? resourceDetails[j]['items'][0]['medias']
                : null,
            },
          ],
        });
      }
      const billableExclusion = selectedExclusions.find((item) =>
        item.quoteBillableId
          ? item.quoteBillableId === billableFormItems[i]['quoteBillableId']
          : item.id === billableFormItems[i]['id']
      );
      billableData.push({
        quoteBillableId: billableFormItems[i]['quoteBillableId'] ?? null,
        billableItemId:
          billableFormItems[i]['billableItemId'] ?? billableFormItems[i]['id'], //This is refrence ID of master billbel table
        name: billableFormItems[i]['name'],
        quantity: parseInt(billableFormItems[i]['quantity']),
        unitTypeId: billableFormItems[i]['unitTypeId']
          ? billableFormItems[i]['unitTypeId']
          : 0,
        exclusion: billableFormItems[i]['exclusion'],
        isExclusionSelected: billableExclusion ? 1 : 0,
        billableTotalPrice: lineTotal.toString(),
        resources: detailsData,
      });
    }
    if (selectedAdhocTerms.length > 0) {
      quoteDataForm['adhocTerms'] = selectedAdhocTerms.map(
        ({ id, adhocTermId, termText }) => ({
          adhocTermId: adhocTermId ?? id,
          termText: termText,
        })
      );
    }

    quoteDataForm['billables'] = billableData.filter(
      (item: any) => item.resources.length > 0
    );
    const endpoint = `${Constants.apiURL + Constants.quoteURL}`;
    let response: any;
    try {
      setInProgress(true);
      response = await HttpService.post(endpoint, quoteDataForm, {
        headers: { Authorization: `Bearer ${getAdmin()}` },
      });
      if (response.success) {
        for (let k = 0; k < fileQuoteMedia.length; k++) {
          await HttpService.handlePresignedURL(
            fileQuoteMedia[k],
            'quote',
            response.data.quote.id,
            getAdmin()
          );
        }
        setInfoDialogOpen(true);
        setInfoMessageTitle(infoSuccessMessages[type]);
        //clearForm(); ̰
      }
    } catch (error: any) {
      console.error(`POST ERROR:`, error);
      setAlert({
        type: 'error',
        message: error.message,
        open: true,
      });
      setInProgress(false);
    }
  };

  async function createRevision() {
    try {
      setInProgress(true);
      const endpoint = `${
        Constants.apiURL + Constants.quoteURL
      }/revision/${quoteId}`;
      const response = await HttpService.post(
        endpoint,
        {},
        {
          headers: { Authorization: `Bearer ${getAdmin()}` },
        }
      );
      if (response.success) {
        setInfoDialogOpen(true);
        setInfoMessageTitle(infoSuccessMessages[9]);
        //clearForm(); ̰
      }
    } catch (error: any) {
      console.error(`GET ERROR:`, error);
      setAlert({
        type: 'error',
        message: error.message,
        open: true,
      });
      setInProgress(false);
    }
  }

  const deleteQuote = async (quoteId: any) => {
    try {
      const response = await HttpService.delete(
        Constants.apiURL + Constants.quoteURL + '/' + quoteId,
        { headers: { Authorization: 'Bearer ' + getAdmin() } },
        {}
      );
      if (response.success) {
        setInfoDialogOpen(true);
        setInfoMessageTitle('The quote has been deleted.');
      }
      console.log('DELETE Response:', response);
    } catch (error: any) {
      console.error(`DELETE ERROR:`, error);
      setAlert({
        type: 'error',
        message: error.message,
        open: true,
      });
    }
  };

  async function updateStatus(quoteId: any, quoteStatusId: any) {
    try {
      setInProgress(true);
      const endpoint = `${Constants.apiURL + Constants.quoteURL}/updateQuoteStatus/${quoteId}`;
      const response = await HttpService.patch(
        endpoint,
        { quoteStatusId: quoteStatusId },
        {
          headers: { Authorization: `Bearer ${getAdmin()}` },
        }
      );
      if (response.success) {
        setInfoDialogOpen(true);
        setInfoMessageTitle(infoSuccessMessages[quoteStatusId]);

        //clearForm(); ̰
      }
    } catch (error: any) {
      console.error(`GET ERROR:`, error);
      setAlert({
        type: 'error',
        message: error.message,
        open: true,
      });
      setInProgress(false);
      if (quoteStatusId === 4) navigate('/quote-dashboard/quote');
    }
  }
  const handleChangeMailBody = (e: any) => {
    const { name, value } = e.target;

    setMailForm((prev: any) => {
      return { ...prev, [name]: value };
    });
  };

  //
  const mailcontentHTML = () => {
    return `
  <div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; padding: 20px; background-color: #f9f9f9;">
    <div style="max-width: 900px; padding: 20px; border-radius: 8px; border: 1px solid #ddd;">
      <p style="font-size: 16px; margin: 0 0 10px;">Dear ${detailForm.mainContact},</p>
      <p style="font-size: 16px; margin: 0 0 10px;">I hope you are doing well.</p>

      <p style="font-size: 16px; margin: 0 0 10px;">
        Thank you for considering <strong>Loumac Constructions</strong> for your upcoming project. The detailed quote for <strong>${
          detailForm.projectName
        }</strong> is attached. We have carefully reviewed your requirements and tailored this quote to meet your specific needs.
      </p>

      <h3 style="margin: 20px 0 10px; font-size: 18px;">Quote Summary:</h3>
      <ul style="padding-left: 20px; margin: 0 0 10px;">
        <li style="margin-bottom: 5px;">Project Name: <strong>${
          detailForm.projectName
        }</strong></li>
        <li style="margin-bottom: 5px;">Total Cost: <strong>$${roundToTwo(roundToTwo(getSubmissionPrice()) + roundToTwo(getGST()))} incl. GST</strong></li>
        <li style="margin-bottom: 5px;">Estimated Start Date: <strong>${getFormatDate(
          detailForm.estimateStartDate
        )}</strong></li>
      </ul>

      <p style="font-size: 16px; margin: 0 0 10px;">
        Please review the attached document for a comprehensive breakdown of the costs and the scope of work. If you have any questions or require further clarification, please contact us. We are more than happy to discuss any aspect of the quote with you.
      </p>

      <p style="font-size: 16px; margin: 0 0 10px;">
        To access further product information, please follow the links provided. Simply click on the link, and it will direct you to the relevant page with detailed information about the products and services included in your quote.
      </p>

      <p style="font-size: 16px; margin: 0 0 10px;">
        To proceed with the project, kindly reply to this email with your approval or any modifications you would like to discuss. Once we receive your confirmation, we will prepare the necessary documentation and schedule a meeting to finalise the details.
      </p>

      <p style="font-size: 16px; margin: 0 0 10px;">
        Thank you for the opportunity to work with you. We look forward to your response and the possibility of bringing your project to life.
      </p>

      <p style="font-size: 16px; font-weight: bold; margin: 0;">Regards,</p>
      <p style="font-size: 16px; margin: 0;">Loumac</p>
    </div>
  </div>

`;
  };
  //dailoge Content for Mailable Send
  const dialogSendMailForApproval = () => {
    return (
      <Box className="mt-2">
        <Grid
          container
          spacing={2}
          style={{
            marginTop: '20px',
            width: '900px',
            border: '3px solid #efeeed',
            borderRadius: '10px',
            backgroundColor: '#f9f9f9',
            padding: '20px',
          }}
        >
          <Grid size={12} sx={{ mt: 2 }}>
            <TextField
              label=" To"
              name="to"
              value={mailForm.to}
              sx={{ width: '100%', background: '#f9f9f9', paddingLeft: '2px' }}
              variant="standard"
              onChange={handleChangeMailBody}
            />
          </Grid>
          <Grid size={12} sx={{ mt: 2 }}>
            <TextField
              label="Subject"
              value={mailForm.subject}
              name="subject"
              sx={{ width: '100%', background: '#f9f9f9', paddingLeft: '2px' }}
              variant="standard"
              onChange={handleChangeMailBody}
            />
          </Grid>
          <Grid size={12}>
            <Box
              dangerouslySetInnerHTML={{ __html: mailcontentHTML() }}
              sx={{}}
            />
          </Grid>
        </Grid>
        <Box sx={{ mt: 2, textAlign: 'right' }}>
          <ButtonComponent
            keyName={'addBtnSend'}
            errors={[]}
            onClick={handleForApproval}
            label={'Send'}
          />
        </Box>
      </Box>
    );
  };
  // dialog Content for Billable List
  const dialogBillableContent = () => {
    return (
      <div
        className="row mb-3 mt-2"
        style={{ display: 'flex', alignItems: 'center', gap: '16px' }}
      >
        <div style={{ flex: 1 }}>
          <SimpleAutoCompleteTextFieldComponent
            name="billableAutoSelect"
            value={currentBillable}
            isRequired={false}
            errorStr={''}
            keyName="billableAutoSelect"
            label="Billable Items"
            onChange={handleBillableAdd}
            items={billableList}
          />
        </div>
        <div>
          <ButtonComponent
            keyName={'addBtnBillable'}
            errors={[]}
            onClick={AddBillableItem}
            label={'Add'}
          />
        </div>
      </div>
    );
  };
  const resourceDialogContent = () => {
    return (
      <div
        className="row mb-3 mt-2"
        style={{ display: 'flex', alignItems: 'center', gap: '16px' }}
      >
        <div style={{ flex: 1 }}>
          <SimpleAutoCompleteTextFieldComponent
            name="resourceLstselect"
            value={currentResource}
            isRequired={false}
            errorStr={''}
            keyName="resourceLstselect"
            label="Resource Items"
            onChange={handleResourceChange}
            items={resourceList}
          />
        </div>
        <div>
          <ButtonComponent
            keyName={'addBtnResourceb'}
            errors={[]}
            onClick={AddResourceItem}
            label={'Add'}
          />
        </div>
      </div>
    );
  };
  return (
    <div>
      <div style={{ margin: '20px' }}>
        <div
          onClick={() => setConfirmDialogOpen(true)}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
        >
          <Typography variant="h6">
            <UTurnLeftIcon
              style={{
                transform: 'rotate(90deg)',
                cursor: 'pointer',
                marginTop: '10px',
              }}
              onClick={() => {
                setConfirmDialogOpen(true);
              }}
            />
          </Typography>
          <Typography variant="h6">Back</Typography>
        </div>

        <Paper
          elevation={3}
          sx={{
            backgroundColor: '#ffffff',
          }}
        >
          {isLoading && (
            <Box sx={{ width: '100%', mt: 3 }}>
              <LinearProgress />
            </Box>
          )}
          <Typography
            variant="h6"
            style={{ paddingLeft: '30px', paddingTop: '30px' }}
          >
            {quoteId !== 'Add' ? 'Update Quote' : 'Add New Quote'}
          </Typography>

          <Box sx={{ padding: '10px', mb: 2, width: '60%', margin: '0 auto' }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label, index: number) => (
                <Step key={label + '_' + index}>
                  <StepLabel
                    onClick={() => {
                      if (
                        activeStep > 0 &&
                        index < activeStep &&
                        (detailForm.quoteStatusId === '1' ||
                          detailForm.quoteStatusId === '2')
                      ) {
                        setActiveStep(index);
                      }
                    }}
                    slotProps={{
                      stepIcon: {
                        sx: {
                          '&.Mui-completed': {
                            color: '#000B29',
                            cursor: 'pointer',
                          },
                          '&.Mui-active': {
                            color: '#00F4FF14', // Active step background color
                            '& .MuiStepIcon-text': {
                              color: '#000000',
                              fill: '#000000',
                              // Active step number color (Step circle)
                            },
                          },
                        },
                      },
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            <Box>
              {isLoading === false && activeStep === 0 && (
                <DetailStep
                  currentCustomer={currentCustomer}
                  customerList={customerListOptions}
                  detailForm={detailForm}
                  handleAutoCompleteChange={handleAutoCompleteChange}
                  handleChangeDate={handleChangeDate}
                  handleDetailInputChange={handleDetailInputChange}
                  inductionList={inductionList}
                  handleInductionChange={handleInductionChange}
                />
              )}
              {isLoading === false && activeStep === 1 && (
                <UploadStep
                  fileQuoteMedia={fileQuoteMedia}
                  handleFileChange={handleFileChange}
                  handleRemoveFile={handleRemoveFile}
                  fileQuoteMediaCurrent={fileQuoteMediaCurrent}
                  handleOpen={(id: any) => {
                    HttpService.handleDownloadFile(id, getAdmin());
                  }}
                  handleCurrentFilesRemove={handleCurrentFilesRemove}
                />
              )}
              {isLoading === false && activeStep === 2 && (
                <PricingStepSummary
                  GST={GST}
                  billableFormItems={billableFormItems}
                  companyDetails={companyDetails}
                  currentProject={currentProject}
                  expandRowState={expandRowState}
                  handleArrow={handleArrow}
                  handleAutoCompleteChange={handleAutoCompleteChange}
                  handleBillableDetailChange={handleBillableDetailChange}
                  projectMargin={projectMargin}
                  handleBillableDialog={(open: boolean) => {
                    setBillableDialogOpen(open);
                  }}
                  quoteStatusId={detailForm.quoteStatusId}
                  handleResourceDialog={(open: boolean, index: number) => {
                    setCurrentBillableIndexResource(index);
                    setResourceDialogOpen(open);
                  }}
                  projectForm={projectForm}
                  projectTemplateList={projectTemplateList}
                  removeDetailsItem={removeDetailsItem}
                  removeItem={removeItem}
                  viewOnly={false}
                  overheads={overheads}
                  overheadPercentage={overheadPercent}
                  handleOverHeads={(val: number) => {
                    setOverheads(val);
                    //  console.log(val, 'in overheads');
                  }}
                  handleOverHeadPercentage={(val: number) => {
                    setOverheadPercent(val);
                    console.log(val, 'in handleOverHeadPercentage');
                  }}
                  projectPercentage={projectPercentage}
                  contigencyPercentage={contigencyPercentage}
                  handleProjectPercentage={(val: number) => {
                    setProjectPercentage(val);
                  }}
                  handleProjectMargin={(val: number) => {
                    setProjectMargin(val);
                  }}
                  handleContigencyPercentage={(val: number) => {
                    console.log('Update New contigencyPercentage ==', val);
                    setContigencyPercentage(val);
                  }}
                  handleAttached={handleAttached}
                  handleOpen={(id) => {
                    HttpService.handleDownloadFile(id, getAdmin());
                  }}
                  handleBillableChange={handleBillableChange}
                  handleBillableBlur={handleBillableBlur}
                />
              )}
              {isLoading === false && activeStep === 3 && (
                <TermsStepTab
                  selectedExclusions={selectedExclusions}
                  adhocTermsList={adhocTermsList}
                  handleTermSelect={handleTermSelect}
                  termsList={standardTermsList}
                  currentStandardTermsList={currentStandardTermsList}
                  billableFormItems={billableFormItems}
                  handleBillableExclusionSelect={handleBillableExclusionSelect}
                  selectedAdhocTerms={selectedAdhocTerms}
                  selectedQuoteInclusion={selectedQuoteInclusion}
                  selectedQuoteExclusion={selectedQuoteExclusion}
                  handleQuoteIncluExclusionSelect={
                    handleQuoteIncluExclusionSelect
                  }
                />
              )}
              {isLoading === false && activeStep === 4 && (
                <ReviewFinalStep
                  detailForm={detailForm}
                  currentCustomer={currentCustomer}
                  currentProject={currentProject}
                  projectForm={projectForm}
                  fileQuoteMedia={fileQuoteMedia}
                  fileQuoteMediaCurrent={fileQuoteMediaCurrent}
                  billableFormItems={billableFormItems}
                  companyDetails={companyDetails}
                  expandRowState={expandRowState}
                  projectTemplateList={projectTemplateList}
                  selectedAdhocTerms={selectedAdhocTerms}
                  selectedQuoteInclusion={selectedQuoteInclusion}
                  selectedQuoteExclusion={selectedQuoteExclusion}
                  termsList={standardTermsList}
                  currentStandardTermsList={currentStandardTermsList}
                  selectedExclusions={selectedExclusions}
                  overheads={overheads}
                  projectMargin={projectMargin}
                  projectPercentage={projectPercentage}
                  contigencyPercentage={contigencyPercentage}
                  GST={GST}
                  fileDownloading={quoteFileDownloading}
                  overheadPercent={overheadPercent}
                  status={getStatusText(detailForm.quoteStatusId)}
                  induction={detailForm.quoteInductionIds}
                  rejectReasonsListTxt={getRejectReasonsListText()}
                  handleDownloadFile={handleDownloadFile}
                ></ReviewFinalStep>
              )}
            </Box>
          </Box>
          {inProgress && (
            <Box sx={{ width: '100%', textAlign: 'center' }}>
              <LinearProgress />
            </Box>
          )}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '25px',
              background: '#F3F6F9',
            }}
          >
            <Button
              variant="contained"
              sx={{ color: '#fff !important' }}
              onClick={handleBack}
              disabled={
                activeStep === 0 ||
                (quoteId !== 'Add' &&
                  detailForm.quoteStatusId !== '1' &&
                  detailForm.quoteStatusId !== '2')
                  ? true
                  : false
              }
            >
              Back
            </Button>

            <Box style={{ display: 'flex', flexDirection: 'row', gap: '24px' }}>
              <Box>
                {detailForm.quoteStatusId === '1' && (
                  <Typography component={'span'}>
                    <Button
                      color="secondary"
                      sx={{ marginRight: '10px' }}
                      variant="contained"
                      onClick={() => {
                        handleFinalSummary(0);
                      }}
                      disabled={isLoading ? true : false}
                    >
                      {'Cancel'}
                    </Button>
                    {quoteId !== 'Add' && (
                      <Button
                        color="secondary"
                        sx={{ marginRight: '10px' }}
                        variant="contained"
                        onClick={() => {
                          handleFinalSummary(-1);
                        }}
                        disabled={isLoading ? true : false}
                      >
                        {'Delete'}
                      </Button>
                    )}
                  </Typography>
                )}
                {(quoteId === 'Add' ||
                  detailForm.quoteStatusId === '1' ||
                  detailForm.quoteStatusId === '1') &&
                  activeStep < 4 && (
                    <Button
                      color="secondary"
                      sx={{ mr: 2 }}
                      variant="contained"
                      onClick={() => {
                        if (validateFields()) {
                          handleFinalSummary(1);
                        }
                      }}
                      disabled={inProgress ? true : false}
                    >
                      {inProgress ? 'Saving...' : 'Save Draft'}
                    </Button>
                  )}

                {activeStep < 4 && (
                  <Button
                    color="primary"
                    sx={{ color: '#fff !important' }}
                    variant="contained"
                    onClick={handleNext}
                    disabled={activeStep === maxSteps - 1 ? true : false}
                  >
                    Next
                  </Button>
                )}
              </Box>
              {activeStep === 4 && (
                <Box>
                  {/* status == draft or in inital step then display this button */}
                  {detailForm.quoteStatusId === '1' && (
                    <Button
                      color="secondary"
                      variant="contained"
                      sx={{ mr: 2 }}
                      onClick={() => {
                        handleFinalSummary(1);
                      }}
                      disabled={inProgress ? true : false}
                    >
                      {inProgress ? 'Saving...' : 'Save Draft'}
                    </Button>
                  )}
                  {/* status == draft or in inital step then display this button */}
                  {detailForm.quoteStatusId === '1' && (
                    <Button
                      disabled={inProgress ? true : false}
                      color="secondary"
                      variant="contained"
                      onClick={() => {
                        handleFinalSummary(2);
                      }}
                    >
                      {inProgress ? 'Saving...' : 'Send for Approval'}
                    </Button>
                  )}

                  {/* status  === review then display this button */}
                  {detailForm.quoteStatusId === '2' && (
                    <Button
                      disabled={inProgress ? true : false}
                      sx={{ mr: 2 }}
                      color="secondary"
                      variant="contained"
                      onClick={() => {
                        handleFinalSummary(3);
                      }}
                    >
                      {inProgress ? 'Saving...' : 'Approve'}
                    </Button>
                  )}
                  {/* status  === review then display this button */}

                  {/* status  === Approved then display this button */}
                  {detailForm.quoteStatusId === '3' && (
                    <span>
                      <Button
                        disabled={inProgress ? true : false}
                        color="secondary"
                        variant="contained"
                        onClick={() => {
                          handleFinalSummary(8);
                        }}
                        sx={{ mr: 2 }}
                      >
                        {inProgress ? 'Saving...' : 'Accept Quote Internally'}
                      </Button>
                      <Button
                        disabled={inProgress ? true : false}
                        color="secondary"
                        variant="contained"
                        onClick={() => {
                          handleFinalSummary(4);
                        }}
                        sx={{ mr: 2 }}
                      >
                        {inProgress ? 'Saving...' : 'Send to Client'}
                      </Button>
                    </span>
                  )}

                  {/* status  === Sent to Client then display this button */}

                  {detailForm.quoteStatusId === '4' && (
                    <Button
                      disabled={inProgress ? true : false}
                      color="secondary"
                      variant="contained"
                      sx={{ mr: 2 }}
                      onClick={() => {
                        handleFinalSummary(6);
                      }}
                    >
                      {inProgress ? 'Saving...' : 'Accept'}
                    </Button>
                  )}

                  {/* status  ===Rejeced then display this button */}
                  {detailForm.quoteStatusId === '5' && (
                    <Button
                      disabled={inProgress ? true : false}
                      color="secondary"
                      variant="contained"
                      onClick={() => {
                        handleFinalSummary(9);
                      }}
                    >
                      {inProgress ? 'Saving...' : 'Create Revision'}
                    </Button>
                  )}
                  {/* status  === review then display this button */}
                  {(detailForm.quoteStatusId === '3' ||
                    detailForm.quoteStatusId === '2') && (
                    <Button
                      disabled={inProgress ? true : false}
                      color="secondary"
                      variant="contained"
                      onClick={() => {
                        handleFinalSummary(5);
                      }}
                    >
                      {inProgress ? 'Saving...' : 'Reject'}
                    </Button>
                  )}
                  {/* status  === review then display this button */}
                  {detailForm.quoteStatusId === '4' && (
                    <Button
                      disabled={inProgress ? true : false}
                      color="secondary"
                      variant="contained"
                      onClick={() => {
                        handleFinalSummary(7);
                      }}
                    >
                      {inProgress ? 'Saving...' : 'Reject'}
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </div>
        </Paper>
      </div>
      <AlertMessageComponent
        type={alert.type}
        open={alert.open}
        handleClose={handleCloseAlert}
        message={alert.message}
      />
      {
        <SimpleDialogComponent
          title={'Billables'}
          dialogContent={dialogBillableContent()}
          open={billableDialogOpen}
          handleClose={() => {
            setBillableDialogOpen(false);
          }}
          width="70vw"
        />
      }
      {
        <SimpleDialogComponent
          title={'Resources'}
          dialogContent={resourceDialogContent()}
          open={resourceDialogOpen}
          handleClose={() => {
            setResourceDialogOpen(false);
          }}
          width="70vw"
        />
      }
      {
        <SimpleDialogComponent
          title={'Send to Client'}
          dialogContent={dialogSendMailForApproval()}
          open={approvalDialogOpen}
          handleClose={() => {
            setApprovalDialogOpen(false);
          }}
        />
      }
      <ConfirmDialog
        handleClose={handleConfirmDialog}
        message="Are you sure, you want to exit?"
        open={confirmDialogOpen}
      ></ConfirmDialog>
      <WarningDialog
        handleClose={handleWarningClose}
        message={warningMessage}
        open={warningDialogOpen}
        buttonTitle={warningButtonTitle}
        title={warningMessageTitle}
        reasonList={rejectReasonList}
      ></WarningDialog>
      <InfoDialog
        handleClose={() => {
          setInfoDialogOpen(false);
          navigate('/quote-dashboard/quote');
        }}
        open={infoDialogOpen}
        title={infoMessageTitle}
      ></InfoDialog>
    </div>
  );
};
export default QuoteDialogComponent;
